/*
 * Cart rules — per-product limits declared through product metafields:
 *   custom.max_quantity           maximum units of a product allowed in the cart
 *   custom.produits_incompatibles products that cannot share the cart with it
 *
 * Liquid publishes both through `snippets/product-cart-rules.liquid` as data
 * attributes on whatever element can trigger an add (product form, quick-add
 * trigger, upsell button) and on every cart line. The cart drawer is
 * re-rendered server-side after each mutation, so its markup is the cart state
 * this module reads — no extra round trip, and always in sync with the drawer
 * the shopper is looking at.
 *
 * Reading that state is a DOM walk, and one shopper action can ask for it
 * several times over (cart.js re-evaluates, then the product form and the
 * quick-add modal each react to `cart:rules-changed`). So the walk is memoised
 * and only invalidated when the cart actually changes — see `refresh`.
 */
(() => {
  const DEFAULTS = {
    maximumLabel: 'Quantité maximale atteinte',
    incompatibleLabel: 'Produits incompatibles',
    maximumMessage: 'Vous ne pouvez pas commander plus de [[quantity]] unité(s) de ce produit.',
    incompatibleMessage: 'Ce produit ne peut pas être commandé en même temps que « [[product]] », déjà présent dans votre panier.',
  };

  const UNLIMITED = Number.POSITIVE_INFINITY;
  const ALLOWED = { status: 'ok', allowed: UNLIMITED, maximum: UNLIMITED, conflict: null, rules: null };

  // The drawer element itself is rendered once by the layout — only its content
  // is swapped — so it is worth holding on to instead of re-querying.
  let drawerElement = null;
  const drawer = () => {
    if (!drawerElement?.isConnected) drawerElement = document.querySelector('[data-cart-drawer]');
    return drawerElement;
  };

  const setting = (key, fallback) => drawer()?.dataset[key] || fallback;

  const labels = {
    get maximum() { return setting('cartRuleMaxLabel', DEFAULTS.maximumLabel); },
    get incompatible() { return setting('cartRuleIncompatibleLabel', DEFAULTS.incompatibleLabel); },
  };

  const parseIds = (value) => {
    if (!value) return [];
    const ids = [];
    for (const part of value.split(',')) {
      const id = Number.parseInt(part, 10);
      if (Number.isFinite(id) && id > 0) ids.push(id);
    }
    return ids;
  };

  const parseMaximum = (value) => {
    const maximum = Number.parseInt(value, 10);
    return Number.isFinite(maximum) && maximum > 0 ? maximum : UNLIMITED;
  };

  // Read the rules declared on an element (or on its closest rule-bearing ancestor).
  const read = (element) => {
    const source = element?.closest?.('[data-cart-rules]');
    if (!source) return null;
    const productId = Number.parseInt(source.dataset.cartRules, 10);
    if (!Number.isFinite(productId)) return null;
    return {
      productId,
      maxQuantity: parseMaximum(source.dataset.maxQuantity),
      incompatibleIds: parseIds(source.dataset.incompatibleIds),
      reason: source.dataset.incompatibleReason || '',
    };
  };

  /*
   * Cart contents keyed by product id — several lines (variants) collapse into
   * one entry. `declaresIncompatibility` lets evaluate() skip the conflict scan
   * for the overwhelmingly common case where nothing in the cart has a rule.
   *
   * Quantities come from `data-line-quantity`, which cart.js keeps aligned with
   * the quantity input while an edit is still optimistic.
   */
  let cartCache = null;

  const invalidate = () => { cartCache = null; };

  const cartState = () => {
    if (cartCache) return cartCache;

    const products = new Map();
    let declaresIncompatibility = false;

    const lines = drawer()?.querySelectorAll('[data-cart-line]') || [];
    for (const line of lines) {
      const data = line.dataset;
      const productId = Number.parseInt(data.cartRules, 10);
      if (!Number.isFinite(productId)) continue;

      const quantity = Number.parseInt(data.lineQuantity, 10) || 0;
      const existing = products.get(productId);
      if (existing) {
        existing.quantity += quantity;
        continue;
      }

      const incompatibleIds = parseIds(data.incompatibleIds);
      if (incompatibleIds.length) declaresIncompatibility = true;
      products.set(productId, {
        id: productId,
        title: data.productTitle || '',
        quantity,
        incompatibleIds,
        reason: data.incompatibleReason || '',
      });
    }

    cartCache = { products, declaresIncompatibility };
    // Safety net: even if a mutation slips past refresh(), the cache never
    // outlives the task that built it.
    queueMicrotask(invalidate);
    return cartCache;
  };

  const cartProducts = () => cartState().products;

  const quantityInCart = (productId) => cartState().products.get(Number(productId))?.quantity || 0;

  // Incompatibility is symmetric: either side may declare the other.
  const findConflict = (productId, incompatibleIds, products) => {
    const declared = incompatibleIds.length ? new Set(incompatibleIds) : null;
    for (const entry of products.values()) {
      if (entry.id === productId) continue;
      if (declared?.has(entry.id) || entry.incompatibleIds.includes(productId)) return entry;
    }
    return null;
  };

  // Either side of an incompatible pair may carry the explanation; the product
  // being added wins when both do.
  const refused = (rules, conflict, maximum) => ({
    status: 'incompatible',
    allowed: 0,
    maximum,
    conflict,
    rules,
    reason: rules.reason || conflict.reason || '',
  });

  /*
   * Verdict for adding `requested` more units of the product described by `rules`.
   *   status 'ok'           -> nothing in the way
   *   status 'incompatible' -> `conflict` is the cart entry that blocks it
   *   status 'max'          -> the cart already holds the maximum
   *   status 'clamped'      -> only `allowed` more units fit
   */
  const evaluate = (target, requested = 1) => {
    const rules = target && typeof target === 'object' && 'productId' in target ? target : read(target);
    if (!rules) return ALLOWED;

    const capped = rules.maxQuantity !== UNLIMITED;
    if (!capped && !rules.incompatibleIds.length) {
      // No rule of its own — it can still be the passive side of somebody
      // else's, but only if something in the cart declares one.
      const { products, declaresIncompatibility } = cartState();
      if (!declaresIncompatibility) return ALLOWED;
      const conflict = findConflict(rules.productId, rules.incompatibleIds, products);
      return conflict ? refused(rules, conflict, UNLIMITED) : ALLOWED;
    }

    const { products } = cartState();
    const conflict = findConflict(rules.productId, rules.incompatibleIds, products);
    if (conflict) return refused(rules, conflict, rules.maxQuantity);

    if (!capped) return { status: 'ok', allowed: UNLIMITED, maximum: UNLIMITED, conflict: null, rules };

    const allowed = Math.max(rules.maxQuantity - (products.get(rules.productId)?.quantity || 0), 0);
    if (allowed <= 0) return { status: 'max', allowed: 0, maximum: rules.maxQuantity, conflict: null, rules };
    if (requested > allowed) return { status: 'clamped', allowed, maximum: rules.maxQuantity, conflict: null, rules };
    return { status: 'ok', allowed, maximum: rules.maxQuantity, conflict: null, rules };
  };

  const message = (verdict) => {
    if (!verdict) return '';
    if (verdict.status === 'incompatible') {
      const sentence = setting('cartRuleIncompatibleMessage', DEFAULTS.incompatibleMessage)
        .replace('[[product]]', verdict.conflict?.title || '');
      // The generic sentence says what is blocked; the metafield says why.
      return verdict.reason ? `${sentence} ${verdict.reason}` : sentence;
    }
    if (verdict.status === 'max' || verdict.status === 'clamped') {
      return setting('cartRuleMaxMessage', DEFAULTS.maximumMessage)
        .replace('[[quantity]]', String(verdict.maximum));
    }
    return '';
  };

  /*
   * Drop the memoised cart state, then let product forms and the quick-add modal
   * re-evaluate against it. cart.js calls this after every mutation, and a
   * single render can mutate more than once, so the notification is coalesced
   * to one per task — the invalidation itself stays synchronous.
   */
  let notificationQueued = false;

  const refresh = () => {
    invalidate();
    if (notificationQueued) return;
    notificationQueued = true;
    queueMicrotask(() => {
      notificationQueued = false;
      document.dispatchEvent(new CustomEvent('cart:rules-changed'));
    });
  };

  window.CartRules = { read, evaluate, message, labels, cartProducts, quantityInCart, refresh, invalidate };

  /*
   * The /cart page posts `updates[]` natively, so nothing clamps its inputs the
   * way the drawer does. Enforce the `max` the template already renders (which
   * folds in custom.max_quantity) before the form leaves the page.
   */
  document.addEventListener('submit', (event) => {
    const form = event.target;
    if (!form.matches?.('.main-cart form')) return;

    let capped = 0;
    form.querySelectorAll('input[name="updates[]"][max]').forEach((input) => {
      const maximum = Number.parseInt(input.max, 10);
      const quantity = Number.parseInt(input.value, 10);
      if (!Number.isFinite(maximum) || !Number.isFinite(quantity) || quantity <= maximum) return;
      input.value = String(maximum);
      capped = maximum;
    });

    if (!capped) return;

    // Show the corrected quantity and why, rather than silently rewriting what
    // the shopper typed on their way to checkout.
    event.preventDefault();
    let notice = form.querySelector('[data-cart-limit-notice]');
    if (!notice) {
      notice = document.createElement('p');
      notice.className = 'main-cart__limit';
      notice.setAttribute('role', 'alert');
      notice.setAttribute('data-cart-limit-notice', '');
      form.querySelector('.main-cart__footer')?.prepend(notice);
    }
    notice.textContent = message({ status: 'max', maximum: capped });
  });
})();
